// SourceDetail.tsx — /bots/:botId/sources/:sid (Task 5, source-detail-diagnostics). The screen
// this whole cycle exists for: what got indexed, and — the part nothing else in the product can
// answer — what did not, and why. A page, not a dialog (design spec §Dashboard): the content
// readback can run long, and a URL that can be pasted into a bug report is worth more here than
// a modal.
//
// Resync/Delete reuse the SAME semantics BotSources.tsx's row actions already carry (Task 3/4):
// Delete has no in-flight guard server-side (deleting mid-crawl is a supported escape hatch, so
// it's disabled ONLY on `status === 'deleting'`), Resync/PATCH 409 `SOURCE_DELETING` while
// mid-teardown, and DELETE's response IS the post-write `SourceDto` — no override needed to show
// `deleting`, a plain invalidate does it. Rather than re-deriving any of that, this file imports
// the exact same `ANY_SYNCING`/`applySourceOverrides`/`shouldKeepSyncing` helpers
// `BotSources.tsx` uses, applied to a one-element array — this page has exactly one row, not a
// table of them, but the "resync enqueues a job the poller hasn't claimed yet" race is
// identical, so the fix is identical.
//
// Review round 1: rendered mode is now a live `RenderedModeToggle` here too (shared with
// `BotSources.tsx`, `../components/RenderedModeToggle`) — the most likely visitor to this page
// just read a truncated/near-empty crawl telling them to try rendered mode, and this screen
// already carries Resync, so the fix belongs beside the diagnosis. That toggle shows the
// source's CURRENT flag; the "Latest crawl" section separately states what mode that RUN
// actually used, since those can disagree the instant the toggle is flipped and before the
// resulting resync finishes — the confusion `CrawlRunDto.renderedMode` exists to prevent.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CrawlRunDto,
  IndexState,
  SourceDetailDto,
  SourceDocumentContentDto,
  SourceDocumentDto,
  SourceDocumentsPageDto,
  SourceDto,
  SourceType,
} from '@repo/shared-types';
import {
  FAILED_URL_SAMPLE_CAP,
  indexingFailureMessage,
  isHintMessage,
  languageName,
  stripHintPrefix,
} from '@repo/shared-types';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Card } from '@repo/ui/components/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { StatusChip, type StatusChipState } from '@repo/ui/components/status-chip';
import { ExternalLinkIcon } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { AdminPagination } from '../components/AdminPagination';
import { PageHeader } from '../components/PageHeader';
import { RenderedModeExplainer, RenderedModeToggle } from '../components/RenderedModeToggle';
import { BILLING_SUMMARY_QUERY_KEY } from '../hooks/useBillingSummary';
import { ApiError, apiFetch } from '../lib/api';
import { describeCrawlRun, isCrawlNarratedError } from '../lib/crawl-run';
import { relativeTime } from '../lib/relative-time';
import { useLocale } from '../locales/context';
import type { Catalog } from '../locales/en';
import type { Locale } from '../locales/index';
import { formatNumber } from '../locales/format';
import {
  ANY_SYNCING,
  applySourceOverrides,
  OVERRIDE_TIMEOUT_MS,
  shouldKeepSyncing,
  type SourceOverride,
  type SourceOverrideBaseline,
} from '../lib/source-overrides';

/** Sized to the real header + facts card + run summary below (handoff Shell Anatomy §06). */
function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <SkeletonText className="w-28" />
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <Card className="gap-3 p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <SkeletonText className="w-16" />
              <SkeletonText className="w-32" />
            </div>
          ))}
        </div>
      </Card>
      <SkeletonText className="w-full max-w-md" />
      <div className="flex flex-col overflow-hidden rounded-lg border">
        <div className="h-9 bg-muted" />
        {[0, 1, 2].map((i) => (
          <div key={i} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
            <SkeletonText className="w-64" />
          </div>
        ))}
      </div>
    </div>
  );
}

function typeLabel(type: SourceDto['type'], t: Catalog): string {
  switch (type) {
    case 'url':
      return t.sourceDetail.typeUrl;
    case 'sitemap':
      return t.sourceDetail.typeSitemap;
    case 'text':
      return t.sourceDetail.typeText;
    case 'file':
      return t.sourceDetail.typeFile;
  }
}

/** Same `hint:`-is-advisory convention `BotSources.tsx` reads off `SourceDto.error`, via the one
 * shared helper (`@repo/shared-types`, final review — this file and `BotSources.tsx` used to
 * each carry their own copy). */
function chipState(status: SourceDto['status'], error: string | null): StatusChipState {
  if (status === 'error' && error && isHintMessage(error)) return 'hint';
  return status;
}

/** content-language design (2026-08-22, THI-119) Task 2 — "Language: Vietnamese (28 pages),
 * English (2)". `null` (renders nothing) when the source has no known language at all —
 * `SourceDto.languages` is `[]`, never omitted, on an undetected/not-yet-crawled source. Only
 * the FIRST entry spells out "pages" (matching the design brief's own worked example
 * byte-for-byte) — every entry after the first is already read in context of that word. */
function languageSummaryText(languages: SourceDto['languages']): string | null {
  if (languages.length === 0) return null;
  return languages
    .map((l, i) =>
      i === 0 ? `${languageName(l.code)} (${l.pages} pages)` : `${languageName(l.code)} (${l.pages})`,
    )
    .join(', ');
}

/** The latest run's "what was skipped" facts — rendered ONLY for the fields that are actually
 * non-empty (ambiguity resolution: never a table of zeros). `failedUrls`/`offSiteRedirects` are
 * both samples capped at `FAILED_URL_SAMPLE_CAP` — the caveat only appears once the sample is
 * actually AT that cap, since below it the list is already the whole truth.
 *
 * `erroredCount`/`totalCount` (index-verification Task 4) are a SEPARATE fact from anything
 * above — CF *indexing* outcomes, not crawl page-load outcomes, so a page can crawl fine and
 * still fail here. Sourced from `SourceDocumentsPageDto.erroredCount`/`totalCount` (source-wide
 * counts, not just the currently viewed page — see that DTO field's own doc comment), rendered
 * through the SAME `indexingFailureMessage` `verify_indexing`'s `partialFailureHint` uses to
 * write the identical fact onto `DataSource.error`, so the two renderers can never drift onto
 * different wording. Not read off `source.error` directly: the counts are the live DB truth,
 * while the hint on `source.error` lags one verify tick. Since the final review round,
 * verify-owned hints are NO LONGER suppressed by `isCrawlNarratedError` — they also reach the
 * page-header subtitle — so on url/sitemap sources this fact deliberately appears twice with
 * byte-identical wording (header + this panel), and on text/file sources (no crawl section)
 * the header is the only place it renders. */
function CrawlDiagnostics({
  run,
  erroredCount,
  totalCount,
  t,
}: {
  run: CrawlRunDto;
  erroredCount?: number;
  totalCount?: number;
  t: Catalog;
}) {
  const hasFailedUrls = run.failedUrls.length > 0;
  const hasRedirects = run.offSiteRedirects.length > 0;
  const hasNearEmpty = run.nearEmptyCount > 0;
  const hasIndexingFailures = (erroredCount ?? 0) > 0;
  if (!hasFailedUrls && !hasRedirects && !hasNearEmpty && !hasIndexingFailures) return null;

  return (
    <div className="flex flex-col gap-4">
      {hasIndexingFailures && (
        <p className="text-[13px] text-muted-foreground">
          {indexingFailureMessage(erroredCount!, totalCount!)}.
        </p>
      )}
      {hasFailedUrls && (
        <div className="flex flex-col gap-1.5">
          <h2 className="text-sm font-semibold text-muted-foreground">
            {t.sourceDetail.failedUrlsHeading}
            {run.failedCount > run.failedUrls.length &&
              t.sourceDetail.failedUrlsCountCaveat({
                total: run.failedCount,
                shown: run.failedUrls.length,
              })}
          </h2>
          <ul className="flex flex-col gap-1 rounded-md border p-3 text-[13px]">
            {run.failedUrls.map((url) => (
              <li key={url} className="truncate font-mono text-xs" title={url}>
                {url}
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasRedirects && (
        <div className="flex flex-col gap-1.5">
          <h2 className="text-sm font-semibold text-muted-foreground">
            {t.sourceDetail.offSiteRedirectsHeading}
            {run.offSiteRedirects.length >= FAILED_URL_SAMPLE_CAP &&
              t.sourceDetail.offSiteRedirectsMoreCaveat}
          </h2>
          <ul className="flex flex-col gap-1.5 rounded-md border p-3 text-[13px]">
            {run.offSiteRedirects.map((r) => (
              <li key={r.url} className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                <span className="truncate font-mono text-xs" title={r.url}>
                  {r.url}
                </span>
                <span className="text-muted-foreground">→</span>
                <span className="truncate font-mono text-xs" title={r.destination}>
                  {r.destination}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasNearEmpty && (
        <p className="text-[13px] text-muted-foreground">
          {t.sourceDetail.nearEmptyCameBack({ n: run.nearEmptyCount })}
        </p>
      )}
    </div>
  );
}

/** On-demand content readback for one document — text/file sources' whole reason for existing
 * in this cycle (design spec defect 3). Fetched lazily, not baked into the list response
 * (`SourceDocumentDto` deliberately never carries `contentMd` — see its doc comment). */
function DocumentContentViewer({
  botId,
  sid,
  docId,
  sourceType,
  t,
}: {
  botId: string;
  sid: string;
  docId: string;
  sourceType: SourceType;
  t: Catalog;
}) {
  const { data, isPending, error } = useQuery({
    queryKey: ['bots', botId, 'sources', sid, 'documents', docId, 'content'],
    queryFn: () =>
      apiFetch<SourceDocumentContentDto>(`/api/bots/${botId}/sources/${sid}/documents/${docId}`),
  });

  if (isPending) {
    return (
      <div className="flex flex-col gap-2 rounded-md border p-4">
        <SkeletonText className="w-full" />
        <SkeletonText className="w-5/6" />
        <SkeletonText className="w-2/3" />
      </div>
    );
  }
  if (error) return <p className="text-destructive">{error.message}</p>;

  // An uploaded file has no markdown of its own: `createFileDocument` (apps/api/src/app.ts)
  // writes `contentMd: ''` deliberately — the raw bytes go to the AI Search instance, which
  // does its own extraction, and to the R2 mirror. Rendering that as an empty `<pre>` reads as
  // "we lost your file" on a source that is indexed and answering (found on dev 2026-08-11).
  // Say what is actually stored instead. Kept as a content-level check rather than a
  // `sourceType === 'file'` branch so a text source that somehow stored nothing says so too.
  if (!data?.contentMd) {
    return (
      <p className="text-muted-foreground">
        {sourceType === 'file'
          ? t.sourceDetail.fileNoMarkdownExplainer
          : t.sourceDetail.nothingStoredForDocument}
      </p>
    );
  }

  return (
    <pre className="overflow-x-auto rounded-md border bg-muted p-4 font-mono text-xs whitespace-pre-wrap">
      {data.contentMd}
    </pre>
  );
}

/** CF's real per-item indexing state (index-verification design, 2026-08-14), distinct from
 * `indexedAt` (upload-accepted, not necessarily indexed) — the fact this whole cycle exists to
 * surface. Mapped onto `StatusChip`'s existing hue vocabulary ("the one way status is rendered
 * anywhere in Embent", that component's own header comment) rather than inventing a fourth: green
 * `ready` for a verified index, blue animated `indexing` for still-in-flight, red `error` for a
 * confirmed CF failure. */
function indexStateChipState(state: IndexState): StatusChipState {
  switch (state) {
    case 'indexed':
      return 'ready';
    case 'pending':
      return 'indexing';
    case 'error':
      return 'error';
  }
}

function indexStateLabel(state: IndexState, t: Catalog): string {
  switch (state) {
    case 'indexed':
      return t.sourceDetail.indexStateIndexed;
    case 'pending':
      return t.sourceDetail.indexStateIndexing;
    case 'error':
      return t.sourceDetail.indexStateFailed;
  }
}

/** `url`/`sitemap` sources: the pages actually indexed, paginated. No content column — a
 * 2,000-page source would otherwise ship its whole corpus into one table (same reasoning as
 * `SourceDocumentDto` never carrying `contentMd`). */
function DocumentsTable({
  documents,
  onPageChange,
  t,
  locale,
}: {
  documents: SourceDocumentsPageDto;
  onPageChange: (page: number) => void;
  t: Catalog;
  locale: Locale;
}) {
  if (documents.totalCount === 0) {
    return <p className="text-muted-foreground">{t.sourceDetail.noPagesIndexedYet}</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t.sourceDetail.colUrl}</TableHead>
            <TableHead>{t.sourceDetail.colCharacters}</TableHead>
            {/* "Status", matching every other status-chip column in the dashboard
                (BotSources.tsx, AdminBots.tsx, …) — "Indexed" would collide, exact-text, with
                the chip's own "Indexed" label rendered directly underneath it. */}
            <TableHead>{t.sourceDetail.colStatus}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.items.map((doc: SourceDocumentDto) => (
            <TableRow key={doc.id}>
              <TableCell className="max-w-96" title={doc.url ?? doc.title ?? undefined}>
                <span className="flex min-w-0 items-center gap-2">
                  {/* THI-99 — the live page, one click away. `doc.url` is null for a text/file
                      document, but `DocumentsTable` only ever renders for url/sitemap sources
                      (see this function's own doc comment), so the plain-text fallback below is
                      the defensive case, not the expected one. `target="_blank"` +
                      `rel="noreferrer"` is the house convention for any outbound link this
                      dashboard renders — never leak a `window.opener` reference or referrer to
                      a page the owner doesn't control. */}
                  {doc.url ? (
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex min-w-0 items-center gap-1 truncate hover:underline"
                    >
                      <span className="truncate">{doc.url}</span>
                      <ExternalLinkIcon
                        className="size-3.5 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </a>
                  ) : (
                    <span className="truncate">{doc.title ?? '—'}</span>
                  )}
                  {/* THI-99 item 2 — computed server-side (`nearEmpty`, `@repo/shared-types`'
                      `SourceDocumentDto`), never re-derived here from `charCount` (untrimmed,
                      so it would disagree with the badge on whitespace-only content). */}
                  {doc.nearEmpty && (
                    <Badge variant="secondary" className="shrink-0 font-normal normal-case">
                      {t.sourceDetail.nearEmptyBadge}
                    </Badge>
                  )}
                </span>
              </TableCell>
              <TableCell>{formatNumber(doc.charCount, locale)}</TableCell>
              <TableCell>
                <span className="flex items-center gap-2">
                  <StatusChip
                    state={indexStateChipState(doc.indexState)}
                    label={indexStateLabel(doc.indexState, t)}
                    title={doc.indexError ?? undefined}
                  />
                  {/* The CF code as visible detail, not just a hover tooltip — same
                      `text-muted-foreground` convention the rest of this screen uses for
                      secondary/explanatory text (`CrawlDiagnostics`'s captions, the header's
                      hint subtitle). */}
                  {doc.indexState === 'error' && doc.indexError && (
                    <span className="text-xs text-muted-foreground">{doc.indexError}</span>
                  )}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <AdminPagination meta={documents} onPageChange={onPageChange} />
    </div>
  );
}

export function SourceDetail() {
  const { t, locale } = useLocale();
  const { botId = '', sid = '' } = useParams<{ botId: string; sid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [docsPage, setDocsPage] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, SourceOverride>>({});
  // THI-99 item 2 — `BotSources.tsx`'s near-empty row hint links here with `?filter=near-empty`
  // (deep link, read once on mount — same "no re-sync with the URL after that" contract
  // `BotConversations.tsx`'s `?open=` deep link follows). Owner-toggleable from here too.
  const [nearEmptyOnly, setNearEmptyOnly] = useState(
    () => searchParams.get('filter') === 'near-empty',
  );

  const {
    data: detail,
    isPending,
    error,
  } = useQuery({
    queryKey: ['bots', botId, 'sources', sid],
    queryFn: () => apiFetch<SourceDetailDto>(`/api/bots/${botId}/sources/${sid}`),
    enabled: !!botId && !!sid,
    // The app's `QueryClient` (main.tsx) has no `retry` override, so this would otherwise
    // default to 3 retries with backoff — a source removed mid-view would sit on the skeleton
    // for ~7s before the 404 (below) ever gets a chance to redirect. A 404 here can never
    // resolve by retrying (the row is gone), so it fails fast; anything else keeps the default
    // retry behavior.
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 3;
    },
    // Same reasoning as BotSources.tsx's own refetchInterval: keep polling while the row (or an
    // unresolved optimistic override on it) is still doing something the poller hasn't reported
    // back on yet.
    refetchInterval: (query) => {
      const source = query.state.data?.source;
      return shouldKeepSyncing(source ? [source] : undefined, overrides) ? 3000 : false;
    },
  });

  // A source removed mid-view (the teardown job finished while this page was open) 404s on the
  // next refetch — the list page's equivalent is the row simply no longer appearing in the
  // array. There's no row to fall out of here, so this page navigates back to where that row
  // used to live instead of showing a dead end.
  useEffect(() => {
    if (error instanceof ApiError && error.status === 404) {
      navigate(`/bots/${botId}/sources`, { replace: true });
    }
  }, [error, botId, navigate]);

  const source = useMemo(() => {
    if (!detail) return undefined;
    return applySourceOverrides([detail.source], overrides)[0];
  }, [detail, overrides]);

  const documentsQuery = useQuery({
    queryKey: ['bots', botId, 'sources', sid, 'documents', docsPage, nearEmptyOnly],
    queryFn: () =>
      apiFetch<SourceDocumentsPageDto>(
        `/api/bots/${botId}/sources/${sid}/documents?page=${docsPage}${nearEmptyOnly ? '&nearEmpty=true' : ''}`,
      ),
    enabled: !!botId && !!sid,
    // Important 3 (final review): without this, the detail query above polls every 3s while
    // syncing but this one never refetches on its own — after a resync that goes 12 → 47 pages,
    // the summary card says "47 pages" while this table still shows the pre-resync page 1, until
    // a remount or window-focus refetch happens to land. Same predicate as the detail query's own
    // `refetchInterval`, closing over `detail?.source` (the RAW, un-overridden source from that
    // query, exactly as `shouldKeepSyncing` expects — see its own doc comment) so both queries
    // start and stop polling in lockstep.
    refetchInterval: () =>
      shouldKeepSyncing(detail?.source ? [detail.source] : undefined, overrides) ? 3000 : false,
  });

  // Flipping the filter changes what "page 1" even means (a near-empty subset can have fewer
  // pages than the full list) — resetting alongside it avoids landing on a page number the
  // filtered result set doesn't have.
  function toggleNearEmptyOnly() {
    setNearEmptyOnly((v) => !v);
    setDocsPage(1);
  }

  function invalidateAll() {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['bots', botId, 'sources', sid] }),
      queryClient.invalidateQueries({ queryKey: ['bots', botId, 'sources'] }),
      queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] }),
      // THI-99 item 1 — same rail `UsageBlock` query BotSources.tsx's mutations invalidate;
      // resync/toggle-rendered-mode/delete all change a source's page count.
      queryClient.invalidateQueries({ queryKey: BILLING_SUMMARY_QUERY_KEY }),
    ]);
  }

  // Same baseline-capture reasoning as BotSources.tsx's captureBaseline — the row's real,
  // observed pre-mutation state, never an assumed one (resync is clickable from `error` too).
  function captureBaseline(): SourceOverrideBaseline {
    if (detail?.source) {
      const s = detail.source;
      return { status: s.status, lastSyncedAt: s.lastSyncedAt, error: s.error };
    }
    return { status: 'pending', lastSyncedAt: '__unknown__', error: '__unknown__' };
  }

  const overrideTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(
    () => () => {
      Object.values(overrideTimeouts.current).forEach(clearTimeout);
    },
    [],
  );
  function scheduleOverrideExpiry(startedAt: number) {
    clearTimeout(overrideTimeouts.current[sid]);
    overrideTimeouts.current[sid] = setTimeout(() => {
      setOverrides((prev) => {
        const current = prev[sid];
        if (!current || current.startedAt !== startedAt) return prev;
        return { ...prev };
      });
    }, OVERRIDE_TIMEOUT_MS + 250);
  }

  const resyncSource = useMutation({
    mutationFn: () => apiFetch<SourceDto>(`/api/bots/${botId}/sources/${sid}/resync`, { method: 'POST' }),
    onMutate: () => {
      const startedAt = Date.now();
      setOverrides((prev) => ({ ...prev, [sid]: { status: 'pending', baseline: captureBaseline(), startedAt } }));
      scheduleOverrideExpiry(startedAt);
      setActionError(null);
    },
    onSuccess: () => invalidateAll(),
    onError: (err: Error) => {
      clearTimeout(overrideTimeouts.current[sid]);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[sid];
        return next;
      });
      // `SOURCE_DELETING`/`SYNC_IN_PROGRESS` already carry an owner-readable message from the
      // route (`failure(code, message)`) — no need for a second layer of code-specific copy.
      setActionError(err.message);
    },
  });

  // Same override as resyncSource (a PATCH here enqueues the exact same resync chain), plus the
  // target `renderedMode` so the toggle shows where it's headed without waiting on the round
  // trip — identical reasoning to `BotSources.tsx`'s own `toggleRenderedMode`.
  const toggleRenderedMode = useMutation({
    mutationFn: (value: boolean) =>
      apiFetch<SourceDto>(`/api/bots/${botId}/sources/${sid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ renderedMode: value }),
      }),
    onMutate: (value) => {
      const startedAt = Date.now();
      setOverrides((prev) => ({
        ...prev,
        [sid]: { status: 'pending', baseline: captureBaseline(), renderedMode: value, startedAt },
      }));
      scheduleOverrideExpiry(startedAt);
      setActionError(null);
    },
    onSuccess: () => invalidateAll(),
    onError: (err: Error) => {
      clearTimeout(overrideTimeouts.current[sid]);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[sid];
        return next;
      });
      setActionError(err.message);
    },
  });

  // Delete needs no override — `DELETE` is an atomic compare-and-swap that returns the
  // post-write `SourceDto` (`status: 'deleting'`) directly, so invalidating fetches the
  // server's own truth immediately (identical reasoning to `BotSources.tsx`'s deleteSource).
  const deleteSource = useMutation({
    mutationFn: () => apiFetch<SourceDto>(`/api/bots/${botId}/sources/${sid}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(),
    onError: (err: Error) => setActionError(err.message),
  });

  if (isPending) return <DetailSkeleton />;
  if (error && !(error instanceof ApiError && error.status === 404)) {
    return <p className="text-destructive">{error.message}</p>;
  }
  if (!detail || !source) return null;

  const runs = detail.runs;
  const latestRun = runs[0];
  const showCrawlSection = source.type === 'url' || source.type === 'sitemap';
  const syncing = ANY_SYNCING.includes(source.status);
  // Review round 1 (Important 4b), corrected in round 2 (the gate was wrong — see
  // `isCrawlNarratedError`'s own doc comment): the header's error line is suppressed ONLY when
  // `source.error` is the crawl's own account of the same event the "Latest crawl" section below
  // already narrates. `DataSource.error` is also written by `index_documents` and the job
  // poller for events no `CrawlRun` mentions at all (an indexing or ingestion failure after a
  // perfectly good crawl) — gating on "a run exists" alone suppressed those too, leaving an
  // `error` chip with no explanation on the one screen built to give one.
  const showHeaderError = !!source.error && !isCrawlNarratedError(source.error);
  // THI-99 item 2 — only offered when there's something to filter FOR: the latest crawl's own
  // near-empty count (the `CrawlDiagnostics` signal above, which `url`/`sitemap` sources always
  // have once crawled) OR any row on the currently loaded page is flagged (covers a source with
  // no crawl-run signal, or a filtered fetch that hasn't resolved the hint check yet).
  // `nearEmptyOnly` itself is included so the control a deep link (`?filter=near-empty`) already
  // turned on never disappears out from under the owner before the page's own data confirms it.
  const showNearEmptyFilter =
    showCrawlSection &&
    ((latestRun?.nearEmptyCount ?? 0) > 0 ||
      (documentsQuery.data?.items.some((d) => d.nearEmpty) ?? false) ||
      nearEmptyOnly);
  const languageSummary = languageSummaryText(source.languages);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={
          // `flex`, not `inline-flex`: an inline-flex sizes to its content, so a long
          // unbroken filename pushed the chip past the h1's `overflow-hidden` and it
          // vanished entirely (found on dev 2026-08-11 with a 60-character PDF name). As a
          // block-level flex the name shrinks and the chip keeps its space.
          <span className="flex min-w-0 items-center gap-3">
            <span className="truncate" title={source.uri}>
              {source.uri}
            </span>
            <span className="shrink-0">
              <StatusChip
                state={chipState(source.status, source.error)}
                label={t.status[chipState(source.status, source.error)]}
              />
            </span>
          </span>
        }
        subtitle={showHeaderError ? stripHintPrefix(source.error!) : undefined}
        action={
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={syncing || resyncSource.isPending}
                onClick={() => resyncSource.mutate()}
              >
                {t.sourceDetail.resyncCta}
              </Button>
              {/* Deliberately NOT gated by `syncing` — see BotSources.tsx's identical Delete
                  button for the full reasoning: DELETE has no in-flight guard server-side, so
                  deleting mid-crawl is a supported escape hatch. Only `status === 'deleting'`
                  itself disables it. */}
              <Button
                type="button"
                variant="destructive-soft"
                size="sm"
                disabled={source.status === 'deleting' || deleteSource.isPending}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                {t.sourceDetail.deleteCta}
              </Button>
            </div>
            {actionError && <p className="text-[13px] text-destructive">{actionError}</p>}
          </div>
        }
      />

      {/* content-language design (2026-08-22, THI-119) Task 2 — nothing renders when the
          source has no known language yet (a brand-new source, or one whose crawl hasn't
          produced a detectable document); `languageSummaryText` returns `null` for that case. */}
      {languageSummary && (
        <p className="text-sm text-muted-foreground">
          {t.sourceDetail.languagePrefix}
          {languageSummary}
        </p>
      )}

      <Card className="gap-3 p-5">
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.sourceDetail.factType}</span>
            <span className="font-medium">{typeLabel(source.type, t)}</span>
          </div>
          {(source.type === 'url' || source.type === 'sitemap') && (
            <div className="flex flex-col gap-1">
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                {t.sourceDetail.factRenderedMode}
                <RenderedModeExplainer t={t} />
              </span>
              <RenderedModeToggle
                checked={source.renderedMode}
                disabled={syncing || toggleRenderedMode.isPending}
                onConfirm={(value) => toggleRenderedMode.mutate(value)}
                t={t}
              />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.sourceDetail.factLastSynced}</span>
            <span className="font-medium">{relativeTime(source.lastSyncedAt, t.time)}</span>
          </div>
        </div>
      </Card>

      {showCrawlSection ? (
        <>
          <div className="flex flex-col gap-1">
            <h2 className="text-sm font-semibold text-muted-foreground">
              {t.sourceDetail.latestCrawlHeading}
            </h2>
            {latestRun ? (
              <>
                <p className="text-sm">{describeCrawlRun(latestRun, t)}</p>
                {/* Important 3 (review round 1) — the header's toggle shows the source's
                    CURRENT flag, which can disagree with what this specific run actually used
                    (flip the toggle, and the current flag changes before the resulting resync
                    finishes). Stating the run's own `renderedMode` here is what
                    `CrawlRunDto.renderedMode`'s doc comment says this column exists to do. */}
                <p className="text-xs text-muted-foreground">
                  {t.sourceDetail.crawlRanInMode({ rendered: latestRun.renderedMode })}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t.sourceDetail.neverCrawledYet}</p>
            )}
          </div>

          {latestRun && (
            <CrawlDiagnostics
              run={latestRun}
              erroredCount={documentsQuery.data?.erroredCount}
              totalCount={documentsQuery.data?.totalCount}
              t={t}
            />
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-muted-foreground">
                {t.sourceDetail.pagesIndexedHeading}
              </h2>
              {/* THI-99 item 2 — hidden entirely on a source with nothing flagged (see
                  `showNearEmptyFilter`'s own doc comment) so a clean source's table isn't
                  cluttered with a control that would just filter to an empty state. Same
                  toggle-button idiom as `BotSources.tsx`'s tab strip (active = filled, inactive
                  = muted hover), reused rather than inventing a checkbox for a two-state
                  choice. */}
              {showNearEmptyFilter && (
                <button
                  type="button"
                  onClick={toggleNearEmptyOnly}
                  aria-pressed={nearEmptyOnly}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    nearEmptyOnly
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  {t.sourceDetail.nearEmptyOnlyToggle}
                </button>
              )}
            </div>
            {documentsQuery.isPending ? (
              <div className="flex flex-col overflow-hidden rounded-lg border">
                <div className="h-9 bg-muted" />
                {[0, 1].map((i) => (
                  <div key={i} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
                    <SkeletonText className="w-64" />
                  </div>
                ))}
              </div>
            ) : documentsQuery.error ? (
              <p className="text-destructive">{documentsQuery.error.message}</p>
            ) : documentsQuery.data ? (
              <DocumentsTable
                documents={documentsQuery.data}
                onPageChange={setDocsPage}
                t={t}
                locale={locale}
              />
            ) : null}
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-muted-foreground">
            {t.sourceDetail.storedContentHeading}
          </h2>
          {documentsQuery.isPending ? (
            <div className="flex flex-col gap-2 rounded-md border p-4">
              <SkeletonText className="w-full" />
              <SkeletonText className="w-5/6" />
            </div>
          ) : documentsQuery.error ? (
            <p className="text-destructive">{documentsQuery.error.message}</p>
          ) : documentsQuery.data && documentsQuery.data.items.length > 0 ? (
            <div className="flex flex-col gap-4">
              {documentsQuery.data.items.map((doc) => (
                <DocumentContentViewer
                  key={doc.id}
                  botId={botId}
                  sid={sid}
                  docId={doc.id}
                  sourceType={source.type}
                  t={t}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">{t.sourceDetail.nothingStoredYet}</p>
          )}
        </div>
      )}

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.sourceDetail.deleteDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.sourceDetail.deleteDialogDescriptionPrefix}
              <span className="font-medium text-foreground">{source.uri}</span>
              {t.sourceDetail.deleteDialogDescriptionSuffix}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              {t.sourceDetail.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                deleteSource.mutate();
                setDeleteConfirmOpen(false);
              }}
            >
              {t.sourceDetail.deleteCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
